import {CheckIcon, CommentIcon, DotFillIcon, FileDiffIcon, MoonIcon, ShieldIcon, SyncIcon} from '@primer/octicons-react'
import {GitHubAvatar} from '@github-ui/github-avatar'
import type {
  ReviewRequestsData,
  CompletedReview,
  PendingReviewRequest,
  Reviewer,
} from '../../page-data/payloads/review-requests'
import {SidebarSection} from './SidebarSection'
import styles from './Reviewers.module.css'
import {pullRequestsOverviewRoute} from '../../routes/overview-route'
import {useSuspenseRouteQuery} from '@github-ui/react-core/future/use-route-query'

import {SkeletonBox, SkeletonText} from '@primer/react/experimental'

interface ReviewersProps {
  reviewers?: ReviewRequestsData
}

export function Reviewers() {
  const {data: reviewers} = useSuspenseRouteQuery(pullRequestsOverviewRoute, 'review_requests')

  return <ReviewersComponent reviewers={reviewers} />
}

/** Reviewers section in the sidebar */
export function ReviewersComponent({reviewers}: ReviewersProps) {
  const completedReviews = reviewers?.completedReviews ?? []
  const pendingRequests = reviewers?.pendingReviewRequests ?? []
  const suggestedReviewers = reviewers?.suggestedReviewers ?? []

  if (completedReviews.length === 0 && pendingRequests.length === 0 && suggestedReviewers.length === 0) {
    return (
      <SidebarSection title="Reviewers">
        <span className={styles.emptyState}>No reviewers</span>
      </SidebarSection>
    )
  }

  return (
    <SidebarSection title="Reviewers">
      <ul className={styles.reviewerList}>
        {completedReviews.map(review => (
          <CompletedReviewRow key={review.reviewer.id} review={review} />
        ))}
        {pendingRequests.map(request => (
          <PendingReviewRow key={request.reviewer.id} request={request} />
        ))}
      </ul>
    </SidebarSection>
  )
}

function ReviewerAvatar({reviewer}: {reviewer: Reviewer}) {
  return <GitHubAvatar src={reviewer.avatarUrl} size={20} square={reviewer.reviewerType === 'Team'} />
}

function ReviewStateIcon({state}: {state: string}) {
  switch (state.toUpperCase()) {
    case 'APPROVED':
      return <CheckIcon size={16} className="color-fg-success" aria-label="Approved" />
    case 'CHANGES_REQUESTED':
      return <FileDiffIcon size={16} className="color-fg-danger" aria-label="Changes requested" />
    case 'COMMENTED':
      return <CommentIcon size={16} className="color-fg-muted" aria-label="Commented" />
    case 'DISMISSED':
      return <ShieldIcon size={16} className="color-fg-muted" aria-label="Dismissed review" />
    default:
      return <DotFillIcon size={16} className="color-fg-attention" aria-label="Pending" />
  }
}

function CompletedReviewRow({review}: {review: CompletedReview}) {
  return (
    <li className={styles.reviewerRow}>
      <ReviewerAvatar reviewer={review.reviewer} />
      <span className={styles.reviewerName}>{review.reviewer.displayLogin}</span>
      <span className={styles.indicators}>
        {review.canReRequest && <SyncIcon size={16} className="color-fg-muted" aria-label="Re-request available" />}
        <ReviewStateIcon state={review.state} />
      </span>
    </li>
  )
}

function PendingReviewRow({request}: {request: PendingReviewRequest}) {
  return (
    <li className={styles.reviewerRow}>
      <ReviewerAvatar reviewer={request.reviewer} />
      <span className={styles.reviewerName}>{request.reviewer.displayLogin}</span>
      <span className={styles.indicators}>
        {request.deferredType !== null && (
          <MoonIcon size={16} className="color-fg-muted" aria-label="Deferred reviewer" />
        )}
        {(request.isCodeOwner || request.isCodeOwnerFromDelegation) && (
          <ShieldIcon size={16} className="color-fg-muted" aria-label="Code owner" />
        )}
        <DotFillIcon size={16} className="color-fg-attention" aria-label="Pending" />
      </span>
    </li>
  )
}

const PLACEHOLDER_ROWS = 3

/** Loading skeleton that mirrors the Reviewers sidebar section */
export function ReviewersSkeleton() {
  return (
    <SidebarSection title="Reviewers">
      <ul className={styles.reviewerList}>
        {Array.from({length: PLACEHOLDER_ROWS}, (_, i) => (
          <li key={i} className={styles.reviewerRow}>
            <SkeletonBox className={styles.avatar} />
            <SkeletonText size="bodySmall" className={styles.name} />
            <SkeletonBox className={styles.indicator} />
          </li>
        ))}
      </ul>
    </SidebarSection>
  )
}
